// src/pages/DevHub/DevHubPage.tsx
// Deep-dive rewrite: DeepDiveSection cards for 4 developer services — matching FeaturesPage design language

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { seoConfig } from '../../config/seo';
import {
    Terminal, ShieldCheck, Activity,
    Database, Webhook, Mail, CreditCard,
    CheckCircle, ArrowRight, LucideIcon,
} from 'lucide-react';
import { m } from 'framer-motion';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Typography } from '@/components/ui/typography';
import { Article } from '../Insights/types';
import { allPosts } from '../Insights/Articles/ArticleData';

// ── Types ────────────────────────────────────────────────────────────────────

interface DeepDiveCard {
    id: string;
    icon: LucideIcon;
    title: string;
    tagline: string;
    description: string;
    useCases: string[];
    keyFacts: { label: string; value: string }[];
    link: string;
    cta: string;
    accentColor: string;
    accentBg: string;
}

// ── Service Data ─────────────────────────────────────────────────────────────

const serviceCards: DeepDiveCard[] = [
    {
        id: 'mock-api',
        icon: Database,
        title: 'Mock API',
        tagline: 'Professional REST sandbox with chaos engineering',
        description:
            'A stateless REST API sandbox that generates realistic, schema-driven response data on every request. Configure delay, HTTP error codes, and chaos parameters (random 5xx injection, network simulation) to stress-test your frontend against real failure modes. No keys, no registration — endpoint is live the moment you open it.',
        useCases: [
            'Prototype a new frontend feature before the real API is built',
            'Test loading states and skeleton UIs with configurable delays',
            'Build E2E tests that always get deterministic data',
            'Demo a product to a client without needing a live backend',
        ],
        keyFacts: [
            { label: 'Data gen', value: 'Auto schema' },
            { label: 'Chaos', value: 'Configurable' },
            { label: 'Auth', value: 'None needed' },
        ],
        link: '/dev-hub/mock-api',
        cta: 'Launch Mock API',
        accentColor: '#1a73e8',
        accentBg: '#e8f0fe',
    },
    {
        id: 'webhook-tester',
        icon: Webhook,
        title: 'Webhook Tester',
        tagline: 'Real-time HTTP callback inspector',
        description:
            'Point any service at your personal webhook endpoint and watch every incoming request arrive in a live log — headers, body, query params, and timing. Define custom response rules so your webhook replies exactly as your integration expects. Essential for debugging CI/CD pipelines, payment callbacks, and third-party integrations.',
        useCases: [
            'Verify a Stripe webhook fires correctly after a test payment',
            'Debug a GitHub Actions webhook without deploying a server',
            'Inspect the exact payload structure a new SaaS sends',
            'Test webhook retry logic by returning configurable error codes',
        ],
        keyFacts: [
            { label: 'Log', value: 'Live real-time' },
            { label: 'Responses', value: 'Configurable rules' },
            { label: 'Body', value: 'JSON & form data' },
        ],
        link: '/dev-hub/webhook-tester',
        cta: 'Open Webhook Tester',
        accentColor: '#1e8e3e',
        accentBg: '#e6f4ea',
    },
    {
        id: 'email-sandbox',
        icon: Mail,
        title: 'Email Sandbox',
        tagline: 'Black-hole SMTP — preview without sending',
        description:
            'A disposable SMTP server that catches outbound emails from your development or staging environment and renders them in a preview inbox. Inspect full HTML rendering, check spam scores, verify attachments, and analyse headers — without ever sending a message to a real recipient. Prevents the classic "oops, sent 500 test emails to customers" incident.',
        useCases: [
            'Preview a new email template across simulated clients',
            'Verify transactional emails fire correctly after a user action',
            'Check spam headers before switching to a production SMTP service',
            'Test bulk sends on staging without a real email quota',
        ],
        keyFacts: [
            { label: 'Real emails', value: 'Never sent' },
            { label: 'HTML preview', value: 'Full render' },
            { label: 'Spam score', value: 'Included' },
        ],
        link: '/dev-hub/email-sandbox',
        cta: 'Open Email Sandbox',
        accentColor: '#9334e6',
        accentBg: '#f3e8fd',
    },
    {
        id: 'payment-simulator',
        icon: CreditCard,
        title: 'Payment Simulator',
        tagline: 'Full payment lifecycle — zero financial risk',
        description:
            'Simulate the complete payment intent lifecycle — create, confirm, fail, refund — in a stateless sandbox that dispatches realistic webhook events to your server. Trigger fraud scenarios, network timeouts, and card decline codes on demand. Build and harden your checkout flow without touching a test account or risking real transactions.',
        useCases: [
            'Test how your order page handles a card decline gracefully',
            'Verify your refund logic fires the correct webhook handler',
            'Simulate a payment network timeout and check retry behaviour',
            'Load-test checkout without generating real payment records',
        ],
        keyFacts: [
            { label: 'Scenarios', value: 'Fraud · timeout · decline' },
            { label: 'Webhooks', value: 'Dispatched' },
            { label: 'Risk', value: 'Zero' },
        ],
        link: '/dev-hub/payment-simulator',
        cta: 'Open Payment Simulator',
        accentColor: '#f9ab00',
        accentBg: '#fef7e0',
    },
];

// ── DeepDive Card Component ───────────────────────────────────────────────────

const DeepDiveSection: React.FC<{ card: DeepDiveCard; index: number }> = ({ card, index }) => {
    const Icon = card.icon;
    const flip = index % 2 !== 0;
    return (
        <m.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: (index % 2) * 0.08 }}
            className="bg-white border border-[#e8eaed] rounded-[32px] overflow-hidden hover:shadow-lg transition-shadow duration-300"
        >
            <div className={`grid grid-cols-1 lg:grid-cols-2 ${flip ? 'lg:[&>div:first-child]:order-2' : ''}`}>
                {/* Left: content */}
                <div className="p-8 md:p-12 flex flex-col justify-between">
                    <div>
                        <div className="flex items-start gap-4 mb-6">
                            <div
                                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 mt-0.5"
                                style={{ backgroundColor: card.accentBg, color: card.accentColor }}
                            >
                                <Icon size={24} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h3 className="text-2xl font-medium text-[#121317] leading-snug">{card.title}</h3>
                                <p className="text-[0.9rem] mt-1" style={{ color: card.accentColor }}>{card.tagline}</p>
                            </div>
                        </div>
                        <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] mb-8">{card.description}</p>
                        <div className="flex flex-wrap gap-3 mb-8">
                            {card.keyFacts.map(f => (
                                <div key={f.label} className="flex flex-col px-4 py-2.5 rounded-2xl border border-[#e8eaed] bg-[#f8f9fa]">
                                    <span className="text-[0.7rem] font-medium text-[#80868b] uppercase tracking-wide">{f.label}</span>
                                    <span className="text-[0.9rem] font-medium text-[#121317]">{f.value}</span>
                                </div>
                            ))}
                        </div>
                        <Link
                            to={card.link}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-white no-underline transition-all hover:opacity-90 hover:shadow-md text-[0.9375rem]"
                            style={{ backgroundColor: card.accentColor }}
                        >
                            {card.cta} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
                {/* Right: use-cases panel */}
                <div
                    className="p-8 md:p-12 flex flex-col justify-center"
                    style={{ backgroundColor: card.accentBg + '55' }}
                >
                    <h4 className="text-[0.75rem] font-semibold uppercase tracking-widest mb-5" style={{ color: card.accentColor }}>
                        Common use cases
                    </h4>
                    <ul className="space-y-4 list-none p-0 m-0">
                        {card.useCases.map((uc, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <CheckCircle size={16} className="shrink-0 mt-0.5" style={{ color: card.accentColor }} />
                                <span className="text-[0.9rem] text-[#3c4043] leading-snug">{uc}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </m.div>
    );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const DevHubPage: React.FC = () => {
    const [randomInsights, setRandomInsights] = useState<Article[]>([]);

    useEffect(() => {
        const shuffled = [...allPosts].sort(() => 0.5 - Math.random());
        setRandomInsights(shuffled.slice(0, 3));
    }, []);

    return (
        <>
            <SEO {...seoConfig.devHub} />
            <div className="min-h-screen bg-white font-brand text-[#202124]">
                <main>
                    {/* ── Hero ───────────────────────────────────────────────── */}
                    <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
                        <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                            Build faster.{' '}<span className="text-[#1a73e8]">Ship better software.</span>
                        </Typography>
                        <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
                            Four professional-grade infrastructure services for building, testing, and hardening
                            your integrations — no setup, no accounts, zero cost.
                        </Typography>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <Link
                                to="/features"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a73e8] text-white font-medium rounded-full no-underline hover:bg-[#1557b0] transition-colors shadow-sm"
                            >
                                See full platform <ArrowRight size={17} />
                            </Link>
                            <Link
                                to="/tools"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-white border border-[#dadce0] text-[#3c4043] font-medium rounded-full no-underline hover:bg-[#f8f9fa] transition-colors"
                            >
                                Professional Tools
                            </Link>
                        </div>
                    </header>

                    {/* ── Stat strip ─────────────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 pb-20 border-b border-[#f1f3f4]">
                        <div className="flex justify-center gap-12 sm:gap-24 flex-wrap">
                            {[
                                { value: '4', label: 'Developer Services' },
                                { value: '0', label: 'Setup Required' },
                                { value: '∞', label: 'Free to Use' },
                                { value: '100%', label: 'Stateless & Private' },
                            ].map(s => (
                                <div key={s.label} className="flex flex-col items-center text-center">
                                    <div className="text-[2.75rem] font-light text-[#121317] leading-none mb-2">{s.value}</div>
                                    <div className="text-[0.875rem] text-[#5f6368]">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── Service Deep-Dive Cards ─────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 py-20">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
                            <div className="max-w-2xl">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#e8f0fe] text-[#1a73e8]">
                                        <Terminal size={18} strokeWidth={1.5} />
                                    </div>
                                    <span className="text-[0.8rem] font-semibold uppercase tracking-widest text-[#1a73e8]">Developer Services</span>
                                </div>
                                <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Four services for builders</h2>
                                <p className="text-[#5f6368] text-lg leading-relaxed">
                                    Stateless, private infrastructure sandboxes — live instantly, no keys or accounts required.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-6">
                            {serviceCards.map((card, i) => (
                                <DeepDiveSection key={card.id} card={card} index={i} />
                            ))}
                        </div>
                    </section>

                    {/* ── Quality Traits ─────────────────────────────────────── */}
                    <section className="bg-white py-20 border-t border-[#f1f3f4]">
                        <div className="max-w-7xl mx-auto px-6">
                            <div className="bg-[#f8f9fa] rounded-[32px] p-8 md:p-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                                    <div className="w-14 h-14 bg-[#e8f0fe] rounded-2xl flex items-center justify-center mb-6">
                                        <Activity size={28} className="text-[#1a73e8]" />
                                    </div>
                                    <h3 className="text-xl font-medium mb-3 text-[#121317]">Chaos Engineering</h3>
                                    <p className="text-[#5f6368] leading-relaxed">
                                        Inject delays and server errors via simple URL parameters to test your app's resilience under realistic service failure conditions
                                    </p>
                                </div>
                                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                                    <div className="w-14 h-14 bg-[#e6f4ea] rounded-2xl flex items-center justify-center mb-6">
                                        <Terminal size={28} className="text-[#1e8e3e]" />
                                    </div>
                                    <h3 className="text-xl font-medium mb-3 text-[#121317]">Stateless & Fast</h3>
                                    <p className="text-[#5f6368] leading-relaxed">
                                        No accounts or database setup required. Instant API endpoints with predictable data structures for rapid development
                                    </p>
                                </div>
                                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                                    <div className="w-14 h-14 bg-[#fef9e7] rounded-2xl flex items-center justify-center mb-6">
                                        <ShieldCheck size={28} className="text-[#f9ab00]" />
                                    </div>
                                    <h3 className="text-xl font-medium mb-3 text-[#121317]">Privacy First</h3>
                                    <p className="text-[#5f6368] leading-relaxed">
                                        Full-privacy sandbox. We don't store your test payloads or monitor your private integrations. Your data stays yours
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── Engineering Guides ─────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 py-24">
                        <div className="flex flex-col md:flex-row justify-between items-baseline mb-12 gap-4">
                            <div>
                                <h2 className="text-3xl font-light text-[#121317] mb-3">Engineering Guides</h2>
                                <p className="text-[#5f6368] text-lg">In-depth technical resources for modern engineering</p>
                            </div>
                            <Link
                                to="/insights/articles"
                                className="text-[#1a73e8] font-medium flex items-center gap-1 hover:underline group"
                            >
                                Browse all articles
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {randomInsights.map((post) => (
                                <Link
                                    key={post.id}
                                    to={`/insights/articles/${post.slug}`}
                                    className="group flex flex-col bg-white border border-[#dadce0] rounded-[32px] overflow-hidden hover:shadow-md transition-all duration-300 no-underline"
                                >
                                    <div className="aspect-[16/10] overflow-hidden bg-[#f8f9fa]">
                                        <OptimizedImage
                                            src={post.imageUrl}
                                            alt={post.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            cinematic
                                        />
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <span className="text-[0.75rem] font-bold text-[#1a73e8] uppercase tracking-wider mb-2">
                                            {post.category.replace(/-/g, ' ')}
                                        </span>
                                        <h3 className="text-xl font-medium text-[#121317] mb-3 line-clamp-2 group-hover:text-[#1a73e8] transition-colors">
                                            {post.title}
                                        </h3>
                                        <p className="text-[0.9rem] text-[#5f6368] line-clamp-2 flex-1">{post.excerpt}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                </main>
            </div>
        </>
    );
};

export default DevHubPage;
